import { useState, useRef, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Maximize2, Minimize2, Stars, X } from 'lucide-react';
import {
  parseISO,
  format,
  isSameDay,
  differenceInDays,
  startOfDay,
  isValid,
} from 'date-fns';
import styles from '../../styles/chat-model.module.css';

const parseMessageDate = (message) => {
  if (!message?.created_at) return null;
  const raw = message.created_at;
  const date = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
  return isValid(date) ? date : null;
};

const formatMessageTime = (date) => format(date, 'h:mm a');

const formatDayLabel = (date) => {
  const today = startOfDay(new Date());
  const dayStart = startOfDay(date);
  const days = differenceInDays(today, dayStart);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return format(date, 'EEEE');
  return format(date, 'MMMM d, yyyy');
};
import { useTranslation } from 'react-i18next';
import { useDeviceType } from 'hooks/useDeviceType';
//child components
import Typewriter, { StaticMarkdown } from './Typewritter';
import MessageActions from './MessageActions';
import ProcessProgress from 'components/process-progress';
//constants
import { chat_end_point } from 'constants/constant.js';
import { hasPermissions } from 'constants/permissionChecker';
//icons
import selectDocumentIcon from 'assets/icons/selectDocument.svg';
import { useUploadChatDocsMutation } from 'redux_store/services/client/documentUpload';
//RTK
import { useSelector, useDispatch } from 'react-redux';
import { chatbotData } from 'redux_store/slices/chatMessage';
import { setChatMessage } from 'redux_store/slices/chatMessage';
import { setPageInfo } from 'redux_store/slices/application';
import { chatQuery, removeMarkersAndJson, stripRegeneratePrefix } from 'constants/chatQuery';
import { getUserDetails } from 'auth/auth';
//components
import {
  useGetUserInfoQuery,
  useIsFirstTimeUserMutation,
} from 'redux_store/services/client/onboarding';
import { useSpeechToText } from 'components/SpeechToText/SpeechToText';
import 'components/SpeechToText/SpeechToText.css';
//redux
import { userRoleData } from 'redux_store/slices/userRole';
import { fetchCurrentUser } from 'services/stytchAuthService';
import { mapChangedFieldsToSections, normalizeSections } from 'utils/aiFieldSections';
// Borrower search + chat context
import {
  useLazyFindBorrowerQuery,
  useGetBorrowerChatContextQuery,
  useCreateNewChatMutation,
  borrowerProfileAPI,
} from '../../redux/services/borrower-profile';
import { useGetPersonalDetailsQuery } from 'redux_store/services/client/profile';
import ChatHistory from './ChatHistory';
import ChatHistorySkeleton from './ChatHistorySkeleton';

// Utility function to normalize names for flexible matching.
function normalizeName(name) {
  if (!name) return '';
  // Lowercase, replace non-alphanumeric with space, collapse spaces, trim
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getApplicationIdByBorrowerName(userProfiles, borrowerName) {
  if (!userProfiles || !borrowerName) return null;
  const normalizedInput = normalizeName(borrowerName);
  return (
    userProfiles.find(
      (profile) =>
        normalizeName(`${profile.firstName} ${profile.lastName}`) === normalizedInput
    )?.applicationId || null
  );
}

const TABLET_MIN_WIDTH = 768;
const TABLET_MAX_WIDTH = 1366;
const TABLET_CHAT_RAIL_WIDTH = '60px';

const ChatModel = () => {
  const WS_LOG_PASSWORD = 'mira-debug-2026';
  const isWSLogUnlocked = () => sessionStorage.getItem('ws_log_unlocked') === 'true';

  const { userId, pendingApplicationId, firstName, lastName, permissions } =
    getUserDetails();
  const userInitials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim() || 'U';
  const role = useSelector(userRoleData);
  const userRole = role?.userRole ?? null;
  const [derivedUserRole, setDerivedUserRole] = useState(null);
  const navigate = useNavigate();
  const [findBorrower] = useLazyFindBorrowerQuery(); // Initialize the lazy query hook
  const [createNewChat] = useCreateNewChatMutation();
  const [isVectorClicks, setVectorClicks] = useState(false);
  const [isNewChat, setIsNewChat] = useState(true);
  const [isNewChatOpens, setNewChatOpens] = useState(false);
  const [chatHistorySelected, setChatHistorySelected] = useState(false);
  const [newChatSelected, setNewChatSelected] = useState(true);
  // True while a chat selected from history is fetching its messages. Drives
  // the pill-skeleton placeholder in the chat panel.
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const [isTabletView, setIsTabletView] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const width = window.innerWidth;
    return width >= TABLET_MIN_WIDTH && width <= TABLET_MAX_WIDTH;
  });
  const [isTabletChatHidden, setIsTabletChatHidden] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [borrowerId, setBorrowerId] = useState(null);
  const { t } = useTranslation(['chat']);
  const { isMobile } = useDeviceType();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const tabletQuery = window.matchMedia(
      `(min-width: ${TABLET_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH}px)`
    );
    const handleTabletChange = (event) => {
      setIsTabletView(event.matches);
    };

    setIsTabletView(tabletQuery.matches);

    if (typeof tabletQuery.addEventListener === 'function') {
      tabletQuery.addEventListener('change', handleTabletChange);
      return () => tabletQuery.removeEventListener('change', handleTabletChange);
    }

    tabletQuery.addListener(handleTabletChange);
    return () => tabletQuery.removeListener(handleTabletChange);
  }, []);

  useEffect(() => {
    window.unlockWSLogs = (pwd) => {
      if (pwd === WS_LOG_PASSWORD) {
        sessionStorage.setItem('ws_log_unlocked', 'true');
        console.log('[chat-model] WS logs unlocked.');
      } else {
        console.warn('[chat-model] Incorrect password.');
      }
    };
    window.lockWSLogs = () => {
      sessionStorage.removeItem('ws_log_unlocked');
      console.log('[chat-model] WS logs locked.');
    };

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        const pwd = prompt('Enter password to unlock WS logs:');
        if (pwd !== null) window.unlockWSLogs(pwd);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      delete window.unlockWSLogs;
      delete window.lockWSLogs;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      return;
    }
    const root = document.documentElement;
    const width = isTabletView
      ? isTabletChatHidden
        ? TABLET_CHAT_RAIL_WIDTH
        : 'var(--chat-aside-width)'
      : isChatExpanded
        ? 'var(--chat-aside-expanded-width)'
        : 'var(--chat-aside-width)';
    root.style.setProperty('--chat-aside-current-width', width);
  }, [isChatExpanded, isMobile, isTabletChatHidden, isTabletView]);

  useEffect(() => {
    if (!isTabletView && isTabletChatHidden) {
      setIsTabletChatHidden(false);
    }
  }, [isTabletChatHidden, isTabletView]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileChatOpen(false);
      return;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    if (isMobileChatOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = previousOverflow;
    }

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isMobile, isMobileChatOpen]);
  
  // Function to redirect to pre-underwriting page
  const redirectToPreUnderwriting = (specificApplicationId) => {
    try {
      // The correct path for a loan officer to view a borrower's profile from the pipeline
      const redirectPath = '/pipeline/lead';

      let appId = specificApplicationId;

      // Fallback to the current application ID if none is provided
      if (!appId) {
        appId = pendingApplicationId || applicationId;
      }

      if (!appId) {
        console.error('No application ID available for redirection.');
        // Optionally, display a message to the user in the chat
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: 'AI',
            content:
              "I'm sorry, but I can't seem to find an active application to show the pre-underwriting for.",
          },
        ]);
        return;
      }

      // Construct the query parameters. The page expects 'applicationId'.
      const queryParams = `?applicationId=${appId}`;

      // Navigate to the borrower's profile page and pass the state to select the correct tab.
      navigate(`${redirectPath}${queryParams}`, { state: { page: 7 } });

      console.log(
        `Redirecting to pre-underwriting page for application ID: ${appId}`
      );
    } catch (error) {
      console.error('Error redirecting to pre-underwriting:', error);
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    // Prefer explicit app-level role mapping from Redux
    if (userRole) {
      if (
        userRole === 'loanOfficer' ||
        userRole === 'loanProcessor' ||
        userRole === 'organization' ||
        userRole === 'branchAdmin' ||
        userRole === 'superAdmin'
      ) {
        // Treat all B2B roles as loanOfficer for chat routing
        setDerivedUserRole('loanOfficer');
      } else {
        setDerivedUserRole('consumer');
      }
      return;
    }

    // Fallback to legacy permission-based detection if role is not set
    if (permissions) {
      if (
        hasPermissions(permissions, 'loanOfficer') ||
        hasPermissions(permissions, 'organization')
      ) {
        setDerivedUserRole('loanOfficer');
      } else {
        setDerivedUserRole('consumer');
      }
    }
  }, [userRole, permissions]);

  useEffect(() => {
    if (derivedUserRole) {
      setIsInitialized(true);
    }
  }, [derivedUserRole]);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [inputValue, setInputValue] = useState({
    sender: 'user',
    content: '',
  });
  const chatRequest = useSelector(chatbotData);
  const dispatch = useDispatch();

  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isTypewriterActive, setIsTypeWriterActive] = useState(0);
  const [isAwaitingBorrowerInfo, setIsAwaitingBorrowerInfo] = useState(false);
  const agentSocketRef = useRef(null);
  const [isMessageStreaming, setIsMessageStreaming] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [chatUploadProgress, setChatUploadProgress] = useState(null);
  const [applicationId, setApplicationId] = useState('');
  const messageStreamRef = useRef(false);
  const chatId = useRef(0);
  const setChatIds = (id) => {
    chatId.current = id || null;
  };
  const isConnectedRef = useRef(false);
  const skipDataRef = useRef(true);
  const startJsonParseRef = useRef(false);
  const lastFourMessagesRef = useRef([]);
  const lastThreeMessageRef = useRef([]);
  const lastFiveMessageRef = useRef([]);
  const lastTwoMessageRef = useRef([]);
  const chatPromptRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isSendingRef = useRef(false);
  const wsTransportModeRef = useRef('b2b_v2');
  const lastWsErrorRef = useRef({ message: null, ts: 0 });
  const handledUploadContextIdsRef = useRef(new Set());
  const lastAutoSentTranscriptRef = useRef('');
  const assistantIdRef = useRef('');
  const threadIdRef = useRef('');
  const websocketConnectCountRef = useRef(0);
  const vadStopTimeoutRef = useRef(null);
  const lastAiRefreshTsRef = useRef(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const dataRef = useRef([]);
  const jsonObject = useRef('');
  const websocketMessage = '%S%Can you please try entering that again.%J%{}%E%';
  const connectionRef = useRef();
  const effectRan = useRef(false);
  const pendingMessagesRef = useRef([]);
  const pendingChatRequestRef = useRef(null);

  const [uploadData, { isLoading }] = useUploadChatDocsMutation();

  // const [messages, setMessages] = useState([
  //   {
  //     sender: "AI",
  //     content: `Hello! I am MIRA, I am your friendly mortgage assistant. I'm excited to guide you through the process of getting a mortgage. 🏠`,
  //   },
  //   { sender: "user", content: "I'm interested in getting a mortgage." },
  //   {
  //     sender: "AI",
  //     content:
  //       "That's fantastic! I'm here to help you every step of the way. Let's start by creating a profile for you. This will help me understand your needs and preferences better. May I ask you a few questions to get started?",
  //   },
  //   { sender: "user", content: "Yes, please go ahead." },
  //   {
  //     sender: "AI",
  //     content:
  //       "Great! Since you've already signed up, I have your name, phone number, and email address. Could you please provide your full current address, including the zip code?",
  //   },
  //   {
  //     sender: "user",
  //     content:
  //       "Yes, everything looks good. I'll enter the sensitive information and then click 'Next'.",
  //   },
  //   {
  //     sender: "AI",
  //     content:
  //       "Excellent! I'll be waiting on the next page to help you with the loan details. See you there!",
  //   },
  // ]);

  const [messages, setMessages] = useState([]);

  // Backfill created_at for any message added without one (e.g. local
  // setMessages calls that build {sender, content} object literals).
  // Centralized here so we don't have to touch the dozens of call sites.
  useEffect(() => {
    if (!messages.length) return;
    if (!messages.some((m) => !m.created_at)) return;
    const now = new Date().toISOString();
    setMessages((prev) =>
      prev.map((m) => (m.created_at ? m : { ...m, created_at: now }))
    );
  }, [messages]);

  // Interleave day dividers with messages, computed once per messages change.
  const messageRows = useMemo(() => {
    const rows = [];
    let lastDate = null;
    messages.forEach((message, index) => {
      const date = parseMessageDate(message);
      const newDay = date && (!lastDate || !isSameDay(date, lastDate));
      if (index === 0 || newDay) {
        const labelDate = date || new Date();
        rows.push({
          kind: 'divider',
          label: formatDayLabel(labelDate),
          key: `divider-${index}`,
        });
        if (date) lastDate = date;
      }
      rows.push({
        kind: 'message',
        message,
        index,
        date,
        key: `message-${index}`,
      });
    });
    return rows;
  }, [messages]);
  const [processStates, setProcessStates] = useState({});
  const [currentMessageId, setCurrentMessageId] = useState(null);
  //const [isTextPasted, setIsTextPasted] = useState(false);
  const setInputValueFunction = (text) => {
    if (/\s/.test(text[0])) return false;
    setIsTyping(false);
    setIsTypeWriterActive((prev) => prev + 1);
    setInputValue((prevInputValue) => ({
      ...prevInputValue,
      content: text,
    }));
    setTimeout(() => {
      if (chatPromptRef.current) {
        chatPromptRef.current.style.height = 'auto';
        const height = chatPromptRef.current.scrollHeight;
        chatPromptRef.current.style.height = `${height < 100 ? height : 100}px`;
      }
    }, 0);
  };
  const handleInputChange = (event) => {
    //setIsTextPasted(false);
    setInputValueFunction(event.target.value);
  };
  /* later to be used to handle the on pase event in input 
  const handleOnPasteEvent = (event) => {
    event.preventDefault(); // Prevent the default paste action
    let pastedText = event.clipboardData.getData('text/plain');
    //pastedText.length > 25 && setIsTextPasted(true);
    setInputValueFunction(pastedText);
  }; */
  const [getUserData] = useIsFirstTimeUserMutation();
  const { data: profileData } = useGetUserInfoQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const isConsumer = derivedUserRole === 'consumer';
  const { data: borrowerProfileData } = useGetPersonalDetailsQuery(
    { applicationId },
    { skip: !isConsumer || !applicationId }
  );
  const profileBorrowerId =
    borrowerProfileData?.data?.borrowerId ||
    borrowerProfileData?.data?.borrower_id ||
    borrowerProfileData?.data?.personal_details?.userProfileId ||
    borrowerProfileData?.data?.personal_details?.userId ||
    null;
  const { data: borrowerContextData } = useGetBorrowerChatContextQuery(undefined, {
    skip: !isConsumer || !!profileBorrowerId,
  });

  useEffect(() => {
    const handleOnline = () => {
      if (isConnectedRef.current !== true) {
        connectionRef?.current && connectionRef.current();
      }
      setIsOnline(true);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [isTypewriterActive, isTyping, messages.length]);

  const firstTimeCheckRef = useRef(false);

  useEffect(() => {
    if (!derivedUserRole) return;
    const portalKey =
      derivedUserRole === 'consumer' ? 'b2c_first_time_checked' : 'b2b_first_time_checked';
    if (firstTimeCheckRef.current) return;
    if (sessionStorage.getItem(portalKey) === '1') return;
    firstTimeCheckRef.current = true;
    sessionStorage.setItem(portalKey, '1');
    fetchUserData();
  }, [derivedUserRole]);

  useEffect(() => {
    const profileAppId = profileData?.data?.user_profiles?.[0]?.applicationId;
    if (!applicationId && profileAppId) {
      setApplicationId(profileAppId);
    }
  }, [profileData, applicationId]);

  useEffect(() => {
    if (!isConsumer) return;
    const resolvedBorrowerId =
      profileBorrowerId ||
      borrowerContextData?.data?.borrower_id ||
      borrowerContextData?.data?.borrowerId ||
      null;
    if (resolvedBorrowerId) {
      setBorrowerId(String(resolvedBorrowerId));
    }
    const contextAppId = borrowerContextData?.data?.application_id;
    if (!applicationId && contextAppId) {
      setApplicationId(String(contextAppId));
    }
    const contextOrgId = borrowerContextData?.data?.organization_id;
    if (contextOrgId) {
      const contextOrgIdStr = String(contextOrgId);
      if (!organizationId || (isConsumer && organizationId !== contextOrgIdStr)) {
        setOrganizationId(contextOrgIdStr);
      }
    }
  }, [isConsumer, profileBorrowerId, borrowerContextData, applicationId, organizationId]);

  const fetchUserData = async () => {
    try {
      const resp = await getUserData();
      if (resp?.data?.data?.pending_application_id) {
        setApplicationId(resp?.data?.data?.pending_application_id);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };
  // Resolve tenant/organization id from the authenticated user
  useEffect(() => {
    const loadOrg = async () => {
      try {
        const resp = await fetchCurrentUser();
        const payload = resp?.data?.data ?? resp?.data ?? {};
        const currentUser = payload.user || payload.current_user || payload;
        const orgId =
          currentUser?.primary_organization_id ||
          currentUser?.primaryOrganizationId ||
          currentUser?.organization_id ||
          currentUser?.organizationId ||
          null;
        if (orgId) {
          setOrganizationId(String(orgId));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ChatModel] Failed to resolve organization id for chat', err);
      }
    };

    loadOrg();
  }, []);

  const buildWebSocketUrl = (userIdValue, orgIdValue, isConsumerValue, preferredPath = null) => {
    const baseUrl = chat_end_point || window.location.origin;
    const searchParams = new URLSearchParams();

    if (userIdValue) {
      searchParams.append('user_id', String(userIdValue));
    }
    if (orgIdValue && isConsumerValue) {
      searchParams.append('organization_id', String(orgIdValue));
    }
    if (orgIdValue && !isConsumerValue) {
      searchParams.append('tenant_id', String(orgIdValue));
    }

    try {
      const url = new URL(baseUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = preferredPath || (isConsumerValue ? '/ws/b2c/ai/chat' : '/api/v1/b2b/ai/ws/chat');
      url.search = searchParams.toString();
      return url.toString();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[chat-model] Failed to parse API base URL for WebSocket', error);
      const origin = window.location.origin.replace(/^http/, 'ws');
      const query = searchParams.toString();
      const path = preferredPath || (isConsumerValue ? '/ws/b2c/ai/chat' : '/api/v1/b2b/ai/ws/chat');
      return `${origin}${path}${query ? `?${query}` : ''}`;
    }
  };

  const buildWebSocketCandidates = (userIdValue, orgIdValue, isConsumerValue) => {
    if (isConsumerValue) {
      return [buildWebSocketUrl(userIdValue, orgIdValue, true, '/ws/b2c/ai/chat')];
    }
    // Keep B2B on legacy WebSocket route for now.
    // The new /api/v1/b2b/ai/ws/chat route is currently mounted behind
    // HTTP-only auth dependencies in backend and can fail for WebSocket upgrades.
    const candidates = [
      buildWebSocketUrl(userIdValue, orgIdValue, false, '/ws/ai/chat'),
    ];
    return [...new Set(candidates)];
  };

  function covertFormJSON(input) {
    const result = {
      page: '',
      data: {},
    };

    for (const key in input) {
      const [page, field] = key.split('.');
      result.page = page;
      result.data[field] = input[key];
    }

    return result;
  }

  const pageNames = {
    UserProfiles: 'personalInfoData',
    ApplicationPurchaseHomes: 'loanInfoData',
    UserAssets: 'assetData',
    UserEmploymentAndIncomes: 'incomeData',
    UserREOs: 'REOData',
    ApplicationDeclarations: 'declarationData',
    UserDemographics: 'demographicsData',
    UserLiabilities: 'liabilitiesData',
  };

  const parseStringToObject = (str) => {
    try {
      const obj = JSON.parse(str);
      if (typeof obj === 'object') {
        const extractedObj = {};
        for (const key in obj) {
          const data = key.split('.');
          if (data?.length > 0) {
            extractedObj[data[data?.length - 1]] = obj[key];
          }
        }
        return extracted.obj;
      }
    } catch (error) {
      // If parsing fails or the object does not match the expected format, return an empty object
    }
    return {};
  };

  const extractPageFromString = (str) => {
    const regex = /{"([^"]*)\.([^"]*)":.*}/;
    const match = str.match(regex);
    if (match && match.length > 2) {
      return match[1];
    }
    return null; // Return null if the pattern doesn't match
  };

  const addStringToChat = (respString) => {
    setMessages((prevMessages) => {
      if (
        prevMessages.length > 0 &&
        prevMessages[prevMessages.length - 1].id === chatId?.current
      ) {
        const lastMessage = prevMessages[prevMessages.length - 1];
        return [
          ...prevMessages.slice(0, -1),
          { ...lastMessage, content: lastMessage.content + respString },
        ];
      } else {
        return [
          ...prevMessages,
          { sender: 'AI', content: respString, id: chatId?.current },
        ];
      }
    });
  };

  const resetChatConfig = async () => {
    setIsMessageStreaming(true);
    messageStreamRef.current = true;
    lastFourMessagesRef.current = [];
    lastThreeMessageRef.current = [];
    lastFiveMessageRef.current = [];
    lastTwoMessageRef.current = [];
    jsonObject.current = '';
  };

  const isB2CChat = derivedUserRole === 'consumer';
  const isChatContextReady = isB2CChat
    ? Boolean(organizationId)
    : Boolean(userId && organizationId);

  const ensureChatSession = async (loanAppIdOverride = null) => {
    if (chatId.current) return chatId.current;

    const loanAppId = loanAppIdOverride || applicationId || null;
    const payload = isB2CChat
      ? {
          consumer_user_id: userId,
          organization_id: organizationId,
          isB2C: true,
        }
      : {
          user_id: userId,
          organization_id: organizationId,
          borrower_id: borrowerId,
          loan_application_id: loanAppId,
          isB2C: false,
        };

    try {
      const response = await createNewChat(payload).unwrap();
      const sessionId =
        response?.id ||
        response?.session_id ||
        response?.chat_id ||
        response?.data?.id ||
        null;
      if (sessionId) {
        chatId.current = sessionId;
      }
      return sessionId;
    } catch (error) {
      console.error('Failed to create chat session:', error);
      return null;
    }
  };

  const sendMessageViaWebSocket = async (
    content,
    { loanAppIdOverride = null, borrowerIdOverride = null, metadata } = {}
  ) => {
    if (isB2CChat) {
      if (!organizationId) {
        throw new Error('Missing organization id for B2C chat.');
      }
    } else if (!userId || !organizationId) {
      throw new Error('Missing user or organization id for B2B chat.');
    }

    await connectWebSocket();

    const socket = agentSocketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket connection is not available.');
    }

    const wsMessage = content;
    const resolvedLoanApplicationId =
      loanAppIdOverride || applicationId || null;
    const resolvedBorrowerId = borrowerIdOverride || borrowerId || null;
    const payload = isB2CChat
      ? {
          message: wsMessage,
          chat_id: chatId.current && chatId.current !== 0 ? chatId.current : null,
          consumer_user_id: userId || null,
          organization_id: organizationId || null,
        }
      : {
          type: 'text',
          chat_id: chatId.current && chatId.current !== 0 ? chatId.current : null,
          message: wsMessage,
          borrower_id: resolvedBorrowerId,
          loan_application_id: resolvedLoanApplicationId,
        };

    if (metadata && typeof metadata === 'object') {
      payload.metadata = metadata;
    }

    if (isWSLogUnlocked()) {
      // eslint-disable-next-line no-console
      console.log('[chat-model] WS sending:', payload);
    }
    socket.send(JSON.stringify(payload));
    return null;
  };

  // const closeWebSocket = () => {
  //   if (socketRef.current) {
  //     socketRef.current.close();
  //     console.log('WebSocket connection closed manually.');
  //   } else {
  //     console.error('WebSocket connection is not established.');
  //   }
  // };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (e, inputValue) => {
    console.log('handleSendMessage triggered'); // GEMINI_DEBUG_LOG
    e && e.preventDefault();
    if (inputValue.content === '') return;
    if (isSendingRef.current) return;
    isSendingRef.current = true;

    const userMessage = { ...inputValue };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputValue({ sender: 'user', content: '' });

    if (!isChatContextReady) {
      setIsTyping(true);
      pendingMessagesRef.current.push(userMessage);
      isSendingRef.current = false;
      return;
    }
    setIsTyping(true); // Set typing state immediately
    
    try {
      // If we are waiting for a borrower's name, this message is the answer.
      if (isAwaitingBorrowerInfo) {
        const borrowerName = userMessage.content;
        setIsAwaitingBorrowerInfo(false);
        setIsTyping(true);

        // Use the real API call via the RTK Query hook
        try {
          const { data: response, error } = await findBorrower({ borrowerName });

          if (response && response.applicationId) {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: 'AI',
                content: `Found application for ${borrowerName}. Redirecting you now...`,
              },
            ]);
            redirectToPreUnderwriting(response.applicationId);
          } else {
            setMessages((prevMessages) => [
              ...prevMessages,
              { sender: 'AI', content: `Sorry, I could not find an application for a borrower named "${borrowerName}".` },
            ]);
          }
          setIsTyping(false);
          isSendingRef.current = false;
        } catch (error) {
          console.error('Error searching for borrower:', error);
          setMessages((prevMessages) => [
            ...prevMessages,
            { sender: 'AI', content: `Sorry, I encountered an error while searching for the borrower.` },
          ]);
          setIsTyping(false);
          isSendingRef.current = false;
        }
        return;
      }

      // --- Main message processing ---
      const messageContent = userMessage.content.toLowerCase();
      const isPreUnderwritingRequest =
        messageContent.includes('pre-underwriting') ||
        messageContent.includes('pre underwriting');

      // --- Role-Based Pre-underwriting Logic ---
      if (isPreUnderwritingRequest) {
        if (derivedUserRole === 'loanOfficer') {
          // --- LOAN OFFICER FLOW ---
          let borrowerName = null;
          // Try to extract name after 'for', 'of', or directly after 'pre underwriting'
          const forMatch = userMessage.content.match(/for\s+([a-zA-Z0-9 .'-]+)/i);
          const ofMatch = userMessage.content.match(/of\s+([a-zA-Z0-9 .'-]+)/i);
          if (forMatch) {
            borrowerName = forMatch[1].trim();
          } else if (ofMatch) {
            borrowerName = ofMatch[1].trim();
          } else {
            // Try to extract name after 'pre underwriting' or 'pre-underwriting'
            const preMatch = userMessage.content.match(/pre[- ]underwriting\s+([a-zA-Z0-9 .'-]+)/i);
            if (preMatch) {
              borrowerName = preMatch[1].trim();
            }
          }

          if (borrowerName) {
            // Name was provided, so search for it in cached user profiles
            setIsTyping(true);
            const userProfiles = profileData?.data?.user_profiles || [];
            console.log('User profiles:', userProfiles);
            const normalizedPromptName = normalizeName(borrowerName);
            const matchedProfile = userProfiles.find(
              (profile) => {
                const fullNameNorm = normalizeName(`${profile.firstName} ${profile.lastName}`);
                const altNameNorm = profile?.alternateName ? normalizeName(profile.alternateName) : '';
                return (
                  fullNameNorm === normalizedPromptName ||
                  (altNameNorm && altNameNorm === normalizedPromptName)
                );
              }
            );
            
            const applicationIdFromCache = matchedProfile?.applicationId;
            
            if (applicationIdFromCache) {
              setMessages((prevMessages) => [
                ...prevMessages,
                {
                  sender: 'AI',
                  content: `Found application for ${borrowerName}. Redirecting you now...`,
                },
              ]);
              redirectToPreUnderwriting(applicationIdFromCache);
              setIsTyping(false);
              isSendingRef.current = false;
            } else {
              // No match in cache, try API call as a fallback
              try {
                findBorrower({ borrowerName }).then(({ data: response, error }) => {
                  if (response && response.applicationId) {
                    setMessages((prevMessages) => [
                      ...prevMessages,
                      {
                        sender: 'AI',
                        content: `Found application for ${borrowerName}. Redirecting you now...`,
                      },
                    ]);
                    redirectToPreUnderwriting(response.applicationId);
                  } else {
                    setMessages((prevMessages) => [
                      ...prevMessages,
                      { sender: 'AI', content: `Sorry, I could not find an application for a borrower named "${borrowerName}".` },
                    ]);
                  }
                  setIsTyping(false);
                  isSendingRef.current = false;
                }).catch(err => {
                  console.error('Error in findBorrower API call:', err);
                  setMessages((prevMessages) => [
                    ...prevMessages,
                    { sender: 'AI', content: `Sorry, I could not find an application for a borrower named "${borrowerName}".` },
                  ]);
                  setIsTyping(false);
                  isSendingRef.current = false;
                });
              } catch (err) {
                console.error('Error trying API fallback:', err);
                setMessages((prevMessages) => [
                  ...prevMessages,
                  { sender: 'AI', content: `Sorry, I could not find an application for a borrower named "${borrowerName}".` },
                ]);
                setIsTyping(false);
                isSendingRef.current = false;
              }
            }
          } else {
            // No name was provided, so ask for it.
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: 'AI',
                content:
                  "Of course. Which borrower's pre-underwriting analysis would you like to see? Please provide their full name.",
              },
            ]);
            setIsAwaitingBorrowerInfo(true);
            setIsTyping(false);
            isSendingRef.current = false;
            return;
          }
        } else {
          // --- BORROWER FLOW ---
          console.log(
            'Pre-underwriting request by borrower. Using standard flow.'
          );
          // For borrowers, we just continue with the normal flow to let the AI handle it
        }
      }
      
      // For non-pre-underwriting requests or if we're continuing with normal flow
      setIsTyping(true);
      await sendMessageViaWebSocket(userMessage.content, {
        loanAppIdOverride: userMessage.loan_application_id || userMessage.loanApplicationId,
        borrowerIdOverride: userMessage.borrower_id || userMessage.borrowerId,
        metadata: userMessage.metadata,
      });
      setIsMessageStreaming(false);
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      Sentry.captureException(error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: 'AI',
          content: 'Unable to send message - chat connection is not available.',
        },
      ]);
      setIsTyping(false);
      isSendingRef.current = false;
    }
  };

  // Ref to always hold the latest handleSendMessage (avoids stale closure in event listener)
  // Regenerate: re-ask the question that produced a given AI answer, prefixed with
  // "Regenerate:" so the agent (RULE 1.6) redoes it better. Lands as a new visible
  // turn at the bottom — works for any message, no session rewind needed.
  const handleRegenerate = (aiIndex) => {
    let question = null;
    for (let i = aiIndex - 1; i >= 0; i--) {
      if (messages[i]?.sender === 'user') {
        question = messages[i].content;
        break;
      }
    }
    if (!question) return;
    const original = question.replace(/^\s*Regenerate:\s*/i, '');
    handleSendMessage(null, { sender: 'user', content: `Regenerate: ${original}` });
  };

  const handleSendMessageRef = useRef(handleSendMessage);
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  });

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setIsDocumentUploading(true);
      setChatUploadProgress({
        status: 'uploading',
        progressPercent: 5,
        fileName: selectedFile?.name || 'document',
      });
      try {
        await uploadData({
          file: selectedFile,
          loanApplicationId: pendingApplicationId || applicationId || undefined,
          borrowerId: borrowerId || undefined,
          chatId: chatId.current || undefined,
          title: 'chatbot upload',
          fileRequestText: (inputValue?.content || '').trim() || chatQuery.documentUploadMessage,
          useConsumerChatWorkflow: derivedUserRole === 'consumer',
          useB2BChatWorkflow: derivedUserRole !== 'consumer',
        }).unwrap();
        fileInputRef.current.value = null;
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        setIsDocumentUploading(false);
        setChatUploadProgress({
          status: 'failed',
          progressPercent: 100,
          fileName: selectedFile?.name || 'document',
        });
      }
    }
  };

  const detectMutationToolCalls = (metadata) => {
    const functionCalls = metadata?.function_calls || [];
    if (!Array.isArray(functionCalls) || functionCalls.length === 0) {
      return [];
    }
    const MUTATION_TOOLS = new Set(['application_agent']);
    return functionCalls
      .filter((fc) => MUTATION_TOOLS.has(fc?.name))
      .map((fc) => fc?.name)
      .filter(Boolean);
  };

  const extractChangedFieldsFromMetadata = (metadata) => {
    const functionCalls = metadata?.function_calls || [];
    if (!Array.isArray(functionCalls) || functionCalls.length === 0) {
      return [];
    }
    const fields = new Set();
    const parseArgs = (rawArgs) => {
      if (!rawArgs) return {};
      if (typeof rawArgs === 'object') return rawArgs;
      if (typeof rawArgs === 'string') {
        try {
          const parsed = JSON.parse(rawArgs);
          return typeof parsed === 'object' && parsed ? parsed : {};
        } catch {
          return {};
        }
      }
      return {};
    };
    const collectKeys = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(collectKeys);
        return;
      }
      if (typeof value !== 'object') return;
      Object.entries(value).forEach(([key, nested]) => {
        if (!key) return;
        fields.add(key);
        collectKeys(nested);
      });
    };
    functionCalls.forEach((call) => {
      const args = parseArgs(call?.args || call?.arguments);
      collectKeys(args);
      if (args?.updated_fields && typeof args.updated_fields === 'object') {
        Object.keys(args.updated_fields).forEach((key) => fields.add(key));
      }
      if (typeof args?.field_name === 'string') {
        fields.add(args.field_name);
      }
      if (typeof args?.field === 'string') {
        fields.add(args.field);
      }
    });
    return Array.from(fields);
  };

  const triggerAiDataRefresh = (detail = {}) => {
    const now = Date.now();
    if (now - lastAiRefreshTsRef.current < 500) {
      return;
    }
    lastAiRefreshTsRef.current = now;
    const changedFields = Array.isArray(detail?.changed_fields)
      ? detail.changed_fields
      : [];
    const fallbackSectionsFromTools =
      Array.isArray(detail?.entities) && detail.entities.includes('application_agent')
        ? ['personal']
        : [];
    const sections = normalizeSections(
      detail?.sections?.length
        ? detail.sections
        : [
            ...mapChangedFieldsToSections(changedFields),
            ...fallbackSectionsFromTools,
          ]
    );
    window.dispatchEvent(
      new CustomEvent('ai-data-updated', {
        detail: {
          ...detail,
          changed_fields: changedFields,
          sections,
          user_role: derivedUserRole,
          timestamp: detail?.timestamp || new Date().toISOString(),
        },
      })
    );
  };

  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      if (
        agentSocketRef.current &&
        agentSocketRef.current.readyState === WebSocket.OPEN
      ) {
        resolve(); // Already open
      } else {
        if (!userId || !organizationId) {
          reject(new Error('Missing user or organization id for chat WebSocket'));
          return;
        }

        const isConsumerChat = derivedUserRole === 'consumer';
        const candidateUrls = buildWebSocketCandidates(
          userId,
          organizationId,
          isConsumerChat
        );
        let resolvedOrRejected = false;

        const tryConnect = (index) => {
          if (index >= candidateUrls.length) {
            if (!resolvedOrRejected) {
              resolvedOrRejected = true;
              reject(new Error('Unable to connect chat WebSocket using available endpoints'));
            }
            return;
          }

          const url = candidateUrls[index];
          // eslint-disable-next-line no-console
          console.log('Creating new WebSocket connection to:', url);

          let didOpen = false;
          const socket = new WebSocket(url);
          agentSocketRef.current = socket;
          const connectTimeout = setTimeout(() => {
            if (!didOpen && socket.readyState !== WebSocket.CLOSED) {
              // eslint-disable-next-line no-console
              console.warn('[chat-model] WebSocket connection timed out:', url);
              socket.close();
            }
          }, 8000);

          socket.onopen = function (event) {
            didOpen = true;
            clearTimeout(connectTimeout);
            // eslint-disable-next-line no-console
            console.log('AI WebSocket connection opened!', event);
            wsTransportModeRef.current = url.includes('/ws/ai/chat')
              ? 'legacy'
              : 'b2b_v2';
            setIsMessageStreaming(false);
            isConnectedRef.current = true;
            reconnectAttemptsRef.current = 0;

            const isB2C = derivedUserRole === 'consumer';
            const handshake = isB2C
              ? {
                  loan_officer_user_id: null,
                  user_id: userId,
                  organization_id: organizationId,
                  borrower_id: borrowerId,
                  loan_application_id: applicationId,
                  user_role: userRole,
                }
              : {
                  type: 'start',
                  chat_id: chatId.current || null,
                  title: 'FundMore AI Voice Session',
                  borrower_id: borrowerId || null,
                  loan_application_id: applicationId || null,
                  loan_officer_user_id: userId || null,
                  organization_id: organizationId || null,
                  user_role: userRole,
                };
            try {
              socket.send(JSON.stringify(handshake));
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Failed to send AI chat handshake', err);
            }

            if (!resolvedOrRejected) {
              resolvedOrRejected = true;
              resolve();
            }
          };

          socket.onclose = function (event) {
            clearTimeout(connectTimeout);
            console.log(
              'WebSocket connection closed.',
              { code: event?.code, reason: event?.reason, wasClean: event?.wasClean, url }
            );
            isConnectedRef.current = false;
            isSendingRef.current = false;
            if (!didOpen) {
              tryConnect(index + 1);
            }
          };

          socket.onerror = function (error) {
            console.error('WebSocket error:', { error, url });
            if (!didOpen) {
              clearTimeout(connectTimeout);
              tryConnect(index + 1);
            }
          };

          socket.onmessage = function (event) {
          let response;
          try {
            response = JSON.parse(event.data);
          } catch (error) {
            console.warn('[chat-model] Received non-JSON WebSocket message', event.data);
            return;
          }

          const responseType = response?.type || response?.response_type;
          const data = response?.data && typeof response.data === 'object' ? response.data : response;
          if (isWSLogUnlocked()) {
            // eslint-disable-next-line no-console
            console.log('[chat-model] WS received:', responseType, data);
          }

          if (responseType === 'interim_transcript' || responseType === 'transcript' || responseType === 'user_transcript') {
            handleTranscriptMessage(response);
            return;
          }

          if (responseType === 'session_ready') {
            const sessionId = data?.session_id || response?.session_id || null;
            if (sessionId) {
              chatId.current = sessionId;
            }
            return;
          }

          if (responseType === 'chat_upload_progress') {
            const uploadStatus = data?.status;
            if (uploadStatus === 'completed' || uploadStatus === 'completed_without_mongo') {
              setIsDocumentUploading(false);
              setChatUploadProgress(null);
              return;
            }
            setChatUploadProgress({
              status: uploadStatus || 'processing',
              progressPercent: data?.progress_percent ?? null,
              fileName: data?.file_name || null,
            });
            if (uploadStatus === 'failed') {
              setIsDocumentUploading(false);
            }
            return;
          }

          if (responseType === 'chat_upload_context') {
            const payload = data?.payload || {};
            const documentId = payload?.document_id;
            if (documentId && handledUploadContextIdsRef.current.has(documentId)) {
              return;
            }
            if (documentId) {
              handledUploadContextIdsRef.current.add(documentId);
              if (handledUploadContextIdsRef.current.size > 200) {
                handledUploadContextIdsRef.current.clear();
              }
            }
            const docName = payload?.doc_name || 'uploaded document';
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: 'user',
                content: `Chat upload request - "${docName}"`,
              },
            ]);

            const metadata = {
              payload_type: 'document_upload',
              document_id: payload?.document_id,
              mongo_document_id: payload?.mongo_document_id,
            };

            sendMessageViaWebSocket(`Chat upload Request - "${docName}"`, {
              metadata,
              loanAppIdOverride: pendingApplicationId || applicationId || null,
              borrowerIdOverride: borrowerId || null,
            }).catch((sendError) => {
              Sentry.captureException(sendError);
            }).finally(() => {
              setIsDocumentUploading(false);
              setChatUploadProgress(null);
            });
            return;
          }

          if (responseType === 'process_start') {
            if (data?.message_id) {
              setIsTyping(true);
              setCurrentMessageId(data.message_id);
              setProcessStates((prev) => ({
                ...prev,
                [data.message_id]: {
                  status: 'active',
                  steps: data.steps || [],
                  messageId: data.message_id,
                },
              }));
            }
            return;
          }

          if (responseType === 'process_step') {
            if (data?.message_id && data?.step_id) {
              setProcessStates((prev) => {
                const current = prev[data.message_id] || { steps: [], status: 'active' };
                const updatedSteps = [...current.steps];
                const stepIndex = updatedSteps.findIndex((s) => s.id === data.step_id);

                if (stepIndex >= 0) {
                  updatedSteps[stepIndex] = {
                    ...updatedSteps[stepIndex],
                    status: data.status,
                    label: data.label ?? updatedSteps[stepIndex].label,
                    icon: data.icon ?? updatedSteps[stepIndex].icon,
                    index:
                      typeof data.step_index === 'number'
                        ? data.step_index
                        : updatedSteps[stepIndex].index,
                    metadata: data.metadata,
                  };
                } else {
                  updatedSteps.push({
                    id: data.step_id,
                    status: data.status,
                    label: data.label,
                    icon: data.icon,
                    index: data.step_index,
                    metadata: data.metadata,
                  });
                }

                return {
                  ...prev,
                  [data.message_id]: {
                    ...current,
                    steps: updatedSteps,
                  },
                };
              });
            }
            return;
          }

          if (responseType === 'process_complete') {
            if (data?.message_id) {
              setProcessStates((prev) => ({
                ...prev,
                [data.message_id]: {
                  ...prev[data.message_id],
                  status: 'complete',
                },
              }));
            }
            window.dispatchEvent(new CustomEvent('process-complete'));
            return;
          }

          if (responseType === 'process_error') {
            if (data?.message_id) {
              setProcessStates((prev) => ({
                ...prev,
                [data.message_id]: {
                  ...prev[data.message_id],
                  status: 'error',
                  error: {
                    code: data.error_code,
                    message: data.error_message,
                    stepId: data.step_id,
                  },
                },
              }));
            }
            return;
          }

          if (responseType === 'process_abort') {
            if (data?.message_id) {
              setProcessStates((prev) => ({
                ...prev,
                [data.message_id]: {
                  ...prev[data.message_id],
                  status: 'aborted',
                  abortReason: data.reason,
                },
              }));
            }
            return;
          }

          if (responseType === 'AI_response') {
            setIsTyping(true);
            let messageContent = data?.message || '';
            const audioContent = data?.audio_content;
            const messageId = data?.message_id;

            const lowerCaseMessage = messageContent.toLowerCase();
            if (
              lowerCaseMessage.includes('pre-underwriting') ||
              lowerCaseMessage.includes('preunderwriting')
            ) {
              const userIdMatch = messageContent.match(/User ID: (\d+)/i);
              if (userIdMatch && userIdMatch[1]) {
                const extractedUserId = userIdMatch[1];
                console.log(
                  `User ID ${extractedUserId} found in AI response. Triggering redirect.`
                );

                setTimeout(() => {
                  redirectToPreUnderwriting(extractedUserId);
                }, 1000);
              }
            }

            if (messageContent?.includes('reading the database...')) {
              const parts = messageContent.split('|');
              if (parts.length > 1) {
                messageContent = parts[1].trim();
              }
            }

            setMessages((prevMessages) => [
              ...prevMessages,
              { sender: 'AI', content: messageContent, id: messageId },
            ]);

            if (messageId) {
              setProcessStates((prev) => {
                const updated = { ...prev };
                delete updated[messageId];
                return updated;
              });
              setCurrentMessageId(null);
            }

            if (audioContent) {
              try {
                const audioData = atob(audioContent);
                const audioBytes = new Uint8Array(audioData.length);
                for (let i = 0; i < audioData.length; i++) {
                  audioBytes[i] = audioData.charCodeAt(i);
                }
                const audioBlob = new Blob([audioBytes], {
                  type: data?.audio_content_type || 'audio/mpeg',
                });
                const audioUrl = URL.createObjectURL(audioBlob);
                const audio = new Audio(audioUrl);
                audio.play();
              } catch (e) {
                console.error('Failed to decode or play audio:', e);
              }
            }

            // Fallback mutation detection from AI_response metadata
            // (in case backend data_updated event is not sent separately)
            const mutationEntities = detectMutationToolCalls(data?.metadata);
            if (mutationEntities.length > 0) {
              const changedFields = extractChangedFieldsFromMetadata(data?.metadata);
              console.log(
                '[chat-model] AI data mutation detected from AI_response metadata:',
                mutationEntities
              );
              triggerAiDataRefresh({
                entities: mutationEntities,
                changed_fields: changedFields,
              });
            }

            setIsTyping(false);
            setIsMessageStreaming(false);
            isSendingRef.current = false;
            // Title is generated async on the backend after the first
            // assistant reply. Delay slightly so the new title lands
            // before the sidebar list is refetched.
            setTimeout(() => {
              dispatch(
                borrowerProfileAPI.util.invalidateTags(['ChatList'])
              );
            }, 2500);
            return;
          }

          if (responseType === 'chat_response') {
            setIsTyping(true);
            const messageContent = data?.message || response?.message || '';
            const messageId = data?.message_id || response?.message_id;
            const mutationEntities = detectMutationToolCalls(data?.metadata || response?.metadata);

            if (messageContent) {
              setMessages((prevMessages) => [
                ...prevMessages,
                { sender: 'AI', content: messageContent },
              ]);
            }

            if (messageId) {
              setProcessStates((prev) => {
                const updated = { ...prev };
                delete updated[messageId];
                return updated;
              });
              setCurrentMessageId(null);
            }

            if (mutationEntities.length > 0) {
              const changedFields = extractChangedFieldsFromMetadata(
                data?.metadata || response?.metadata
              );
              console.log(
                '[chat-model] AI data mutation detected from chat_response metadata:',
                mutationEntities
              );
              triggerAiDataRefresh({
                entities: mutationEntities,
                changed_fields: changedFields,
              });
            }

            setIsTyping(false);
            setIsMessageStreaming(false);
            isSendingRef.current = false;
            return;
          }

          if (responseType === 'response_audio') {
            const audioB64 = data?.audio_base64 || response?.audio_base64;
            const contentType = data?.content_type || response?.content_type || 'audio/mpeg';
            if (!audioB64) {
              return;
            }
            try {
              const audioData = atob(audioB64);
              const audioBytes = new Uint8Array(audioData.length);
              for (let i = 0; i < audioData.length; i++) {
                audioBytes[i] = audioData.charCodeAt(i);
              }
              const audioBlob = new Blob([audioBytes], { type: contentType });
              const audioUrl = URL.createObjectURL(audioBlob);
              const audio = new Audio(audioUrl);
              audio.play();
            } catch (e) {
              console.error('Failed to decode or play audio:', e);
            }
            return;
          }

          if (responseType === 'speech_activity') {
            const activityEvent = data?.event || response?.event;
            if (activityEvent === 'end' && isRecording && derivedUserRole !== 'consumer') {
              if (vadStopTimeoutRef.current) {
                clearTimeout(vadStopTimeoutRef.current);
              }
              vadStopTimeoutRef.current = setTimeout(() => {
                stopRecording(agentSocketRef.current, null, {
                  mode: wsTransportModeRef.current,
                  sessionId: chatId.current || null,
                });
              }, 500);
            }
            return;
          }

          if (responseType === 'data_updated') {
            console.log('[chat-model] AI data mutation detected:', data);
            triggerAiDataRefresh({
              entities: data?.entities || [],
              changed_fields: data?.changed_fields || [],
              sections: data?.sections || [],
              borrower_id: data?.borrower_id,
              timestamp: data?.timestamp,
            });
            return;
          }

          if (responseType === 'form_json') {
            const result = covertFormJSON(data);
            dispatch(
              setPageInfo({
                name: pageNames[result?.page],
                data: result?.data,
              })
            );
            return;
          }

          if (responseType === 'chat_created') {
            chatId.current = data?.chat_id;
            console.log('Chat created with ID:', chatId.current);
            return;
          }

          if (responseType === 'reasoning') {
            if (data?.message) {
              console.debug('[chat-model] AI reasoning event:', data.message);
            }
            return;
          }

          if (responseType === 'warning') {
            const warningMessage = data?.message || response?.message;
            if (warningMessage) {
              console.warn('[chat-model] WebSocket warning:', warningMessage);
            }
            return;
          }

          if (responseType === 'error') {
            const errorMessage = data?.message || response?.message;
            if (errorMessage) {
              console.error('[chat-model] WebSocket error:', errorMessage);
              const now = Date.now();
              const isDuplicateRapidError =
                lastWsErrorRef.current.message === errorMessage &&
                now - lastWsErrorRef.current.ts < 1500;
              lastWsErrorRef.current = { message: errorMessage, ts: now };
              if (!isDuplicateRapidError) {
                setMessages((prevMessages) => [
                  ...prevMessages,
                  {
                    sender: 'AI',
                    content: errorMessage,
                  },
                ]);
              }
            }
            setIsTyping(false);
          }
        };
        };

        tryConnect(0);
      }
    });
  };

  // WebSocket connection is used for chat + speech; connect once prerequisites are ready.
  useEffect(() => {
    if (!userId || !organizationId || !derivedUserRole) {
      return;
    }
    connectWebSocket().catch((error) => {
      console.error('[chat-model] Failed to connect WebSocket:', error);
    });
  }, [userId, organizationId, derivedUserRole]);

  // Process queued messages once chat context is ready
  useEffect(() => {
    if (!isChatContextReady) return;

    // Process pending user messages
    if (pendingMessagesRef.current.length > 0) {
      const pending = [...pendingMessagesRef.current];
      pendingMessagesRef.current = [];
      pending.forEach((msg) => {
        handleSendMessageRef.current(null, msg);
      });
    }

    // Process pending chat request
    if (pendingChatRequestRef.current) {
      const chatMsg = pendingChatRequestRef.current;
      pendingChatRequestRef.current = null;
      const processPending = async () => {
        try {
          await resetChatConfig();
          await sendMessageViaWebSocket(chatMsg.code, {
            loanAppIdOverride: chatMsg.applicationId,
            borrowerIdOverride: chatMsg.borrowerId,
            metadata: chatMsg.metadata,
          });
        } catch (error) {
          Sentry.captureException(error);
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              sender: 'AI',
              content: 'Unable to send message - chat connection is not available.',
            },
          ]);
          setIsTyping(false);
        }
      };
      processPending();
    }
  }, [isChatContextReady]);

  useEffect(() => {
    const fetchData = async () => {
      if (chatRequest.chatMessage) {
        if (!isChatContextReady) {
          setIsTyping(true);
          setMessages((prev) => [
            ...prev,
            { sender: 'user', content: chatRequest.chatMessage.message },
          ]);
          pendingChatRequestRef.current = chatRequest.chatMessage;
          dispatch(setChatMessage(''));
          return;
        }
        setIsTyping(true);
        setMessages([
          ...messages,
          {
            sender: 'user',
            content: chatRequest.chatMessage.message,
          },
        ]);
        const input = chatRequest.chatMessage.code;

        try {
          await resetChatConfig();
          await sendMessageViaWebSocket(input, {
            loanAppIdOverride: chatRequest.chatMessage.applicationId,
            borrowerIdOverride: chatRequest.chatMessage.borrowerId,
            metadata: chatRequest.chatMessage.metadata,
          });
          dispatch(setChatMessage(''));

          // const res = await axios.post(
          //   `${chat_end_point}agents/orchestrate_agents`,
          //   {
          //     user_input: input,
          //     user_name: 'adom',
          //     userId: userId,
          //     userProfileId: chatRequest.chatMessage.userProfileId,
          //     applicationId: chatRequest.chatMessage.applicationId,
          //   }
          // );

          // dispatch(setChatMessage(''));

          // if (res.data.agent_response) {
          //   setMessages((prevMessages) => [
          //     ...prevMessages,
          //     { sender: 'AI', content: res.data.agent_response },
          //   ]);
          // }
        } catch (error) {
          Sentry.captureException(error);
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              sender: 'AI',
              content: 'Unable to send message - chat connection is not available.',
            },
          ]);
          setIsTyping(false);
        }
      }
    };

    fetchData();
  }, [chatRequest]);
  //remove the json data flag if present
  const getParseText = (text) => {
    if (typeof text === 'string') {
      const newText = text?.replace(/%J%/g, '');
      return newText?.replace(/%J/g, '');
    }
    return text;
  };

  const handleKeyPress = (event) => {
    console.log('handleKeyPress triggered'); // GEMINI_DEBUG_LOG
    if (/\s/.test(event.target.value[0])) return false;
    if (isLoading || isMessageStreaming || isTyping || isSendingRef.current)
      return false;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      setIsTyping(false);
      if (chatPromptRef.current) {
        chatPromptRef.current.style.height = 'auto';
      }
      handleSendMessage(null, inputValue);
    }
  };

  const {
    isRecording,
    error: speechError,
    startRecording,
    stopRecording,
  } = useSpeechToText();

  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].sender === 'user') {
      setIsTyping(true);
    }
  }, [messages]);

  const [transcriptText, setTranscriptText] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  
  // Handler for WebSocket messages including interim transcripts
  const handleTranscriptMessage = (message) => {
    const messageType = message?.type || message?.response_type;
    const payload = message?.data && typeof message.data === 'object' ? message.data : message;
    const transcriptValue = payload?.message || payload?.transcript || '';

    if (messageType === 'interim_transcript') {
      setInterimTranscript(transcriptValue);
    } else if (
      (messageType === 'user_transcript' || messageType === 'transcript') &&
      (payload?.is_final === undefined || payload?.is_final === true)
    ) {
      setTranscriptText(transcriptValue);
      setInterimTranscript("");
      // Display transcript as user message (backend already processes it and generates AI response)
      if (!isRecording && transcriptValue && transcriptValue.trim() !== "") {
        const normalizedTranscript = transcriptValue.trim();
        if (lastAutoSentTranscriptRef.current === normalizedTranscript) {
          return;
        }
        lastAutoSentTranscriptRef.current = normalizedTranscript;
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'user', content: normalizedTranscript },
        ]);
        setInputValue({ sender: 'user', content: '' });
        setIsTyping(true);
        setTranscriptText("");
      }
    }
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      lastAutoSentTranscriptRef.current = '';
      stopRecording(agentSocketRef.current, null, {
        mode: isB2CChat ? 'legacy' : wsTransportModeRef.current,
        sessionId: chatId.current || null,
      });
    } else {
      // Clear any existing transcript when starting a new recording
      setTranscriptText("");
      setInterimTranscript("");
      lastAutoSentTranscriptRef.current = '';
      if (vadStopTimeoutRef.current) {
        clearTimeout(vadStopTimeoutRef.current);
        vadStopTimeoutRef.current = null;
      }
      try {
        await connectWebSocket();
        startRecording(
          agentSocketRef.current,
          userId,
          chatId.current,
          firstName,
          applicationId,
          handleTranscriptMessage,
          {
            mode: isB2CChat ? 'legacy' : wsTransportModeRef.current,
            sessionId: chatId.current || null,
          }
        );
      } catch (error) {
        console.error("Failed to connect WebSocket for recording:", error);
      }
    }
  };
  
  const handleSendTranscript = () => {
    if (transcriptText && !isTyping) {
      setIsTyping(true); // Set before adding message
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'user', content: transcriptText }
      ]);
      setTranscriptText("");
      // Optionally, call handleSendMessage or send to backend here if needed
    }
  };

  useEffect(() => {
    return () => {
      if (vadStopTimeoutRef.current) {
        clearTimeout(vadStopTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message;
      if (!msg) return;
      const syntheticInput = { sender: 'user', content: msg, borrower_id: e.detail?.borrowerId || null };
      handleSendMessageRef.current(null, syntheticInput);
    };
    window.addEventListener('send-chat-message', handler);
    return () => window.removeEventListener('send-chat-message', handler);
  }, []);

  useEffect(() => {
    const openHandler = () => setIsMobileChatOpen(true);
    window.addEventListener('open-mobile-chat', openHandler);
    return () => window.removeEventListener('open-mobile-chat', openHandler);
  }, []);

  if (!isInitialized) {
    return null;
  }

  // Show chatbot for any authenticated user role (B2B + B2C)
  const isChatEnabled = Boolean(userRole || applicationId);

  if (!isChatEnabled) {
    return null;
  }

  const renderChatPanel = ({ isDesktop, isTablet, onClose, onTabletClose }) => (
    <>
      <div className={styles.chatHeader}>
        <div className={styles.logoRow}>
          <img
            src="/fundmoreai.png"
            alt="fundmore.ai"
            className={styles.logoIcon}
          />
        </div>
        <div className={styles.chatHeaderActions}>
          {isMobile ? (
            <button
              type="button"
              className={styles.expandButton}
              onClick={onClose}
              aria-label="Close chat"
            >
              <X className={styles.expandIcon} />
            </button>
          ) : isTablet ? (
            <button
              type="button"
              className={styles.expandButton}
              onClick={onTabletClose}
              aria-label="Close chat"
              title="Close"
            >
              <X className={styles.expandIcon} />
            </button>
          ) : isDesktop ? (
            <button
              type="button"
              className={`${styles.expandButton} ${
                isChatExpanded ? styles.expandButtonActive : ''
              }`}
              onClick={() => setIsChatExpanded((prev) => !prev)}
              aria-label={
                isChatExpanded ? 'Collapse chat sidebar' : 'Expand chat sidebar'
              }
              title={isChatExpanded ? 'Collapse' : 'Expand'}
            >
              {isChatExpanded ? (
                <Minimize2 className={styles.expandIcon} />
              ) : (
                <Maximize2 className={styles.expandIcon} />
              )}
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={`${styles.chatContent} ${styles.scrollbarHide}`}
        ref={chatContainerRef}
      >
          <div className={styles.chatContentInner}>
            {isHistoryLoading && messages.length === 0 && (
              <ChatHistorySkeleton />
            )}
            {messageRows.map((row) => {
              if (row.kind === 'divider') {
                return (
                  <div key={row.key} className={styles.dayDivider}>
                    <span className={styles.dayLabel}>{row.label}</span>
                  </div>
                );
              }
              const { message, date } = row;
              const isUser = message.sender === 'user';
              return (
                <div
                  key={row.key}
                  className={`${styles.messageRow} ${
                    isUser ? styles.messageRowReverse : ''
                  }`}
                >
                  {isUser ? (
                    <div className={`${styles.avatar} ${styles.avatarUser}`}>
                      <span className={styles.avatarInitials}>{userInitials}</span>
                    </div>
                  ) : (
                    <div className={`${styles.avatar} ${styles.avatarBot}`}>
                      <img
                        src="/fundmoreai.png"
                        alt="Mira AI"
                        className={styles.avatarImg}
                      />
                    </div>
                  )}

                  <div
                    className={`${styles.messageGroup} ${
                      isUser ? styles.messageGroupUser : ''
                    }`}
                  >
                    <div
                      className={`${styles.messageBubble} ${
                        isUser ? styles.messageBubbleUser : styles.messageBubbleBot
                      }`}
                    >
                      {isUser ? (
                        <p>{stripRegeneratePrefix(message.content)}</p>
                      ) : message.isHistorical ? (
                        <StaticMarkdown text={message?.content} />
                      ) : (
                        <Typewriter
                          text={message?.content}
                          delay={5}
                          setIsTypeWriterActive={setIsTypeWriterActive}
                          setTyping={() => {
                            setIsTyping(false);
                          }}
                        />
                      )}
                    </div>
                    {!isUser && (
                      <MessageActions
                        content={message?.content}
                        messageId={message?.id}
                        sessionId={chatId.current}
                        isB2C={isConsumer}
                        initialFeedback={message?.feedback}
                        onRegenerate={() => handleRegenerate(row.index)}
                      />
                    )}
                    {message.suggestions && (
                      <div className={styles.suggestionRow}>
                        {message.suggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            className={styles.suggestionButton}
                            onClick={() => setInputValue(suggestion)}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    {date && (
                      <span
                        className={`${styles.timestamp} ${
                          isUser ? styles.timestampUser : styles.timestampBot
                        }`}
                      >
                        {formatMessageTime(date)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {isTyping && (
              <div className={styles.thinkingContainer}>
                <div className={styles.thinkingWrapper}>
                  <div className={`${styles.avatar} ${styles.avatarBot}`}>
                    <img
                      src="/fundmoreai.png"
                      alt="Mira AI"
                      className={styles.avatarImg}
                    />
                  </div>
                  <ProcessProgress
                    processState={
                      currentMessageId && processStates[currentMessageId]
                        ? processStates[currentMessageId]
                        : { status: 'active', steps: [{ id: 'thinking', label: 'Sending your message', icon: 'send', status: 'active', index: 0 }] }
                    }
                    hideProgressBar
                  />
                </div>
              </div>
            )}

            {(isDocumentUploading || chatUploadProgress) && (
              <div className={styles.thinkingContainer}>
                <div className={styles.thinkingWrapper}>
                  <div className={`${styles.avatar} ${styles.avatarBot}`}>
                    <img
                      src="/fundmoreai.png"
                      alt="Mira AI"
                      className={styles.avatarImg}
                    />
                  </div>
                  <div className={styles.uploadProgressBlock}>
                    <div className={styles.uploadProgressTitle}>
                      {chatUploadProgress?.status === 'completed'
                        ? 'Document uploaded'
                        : chatUploadProgress?.status === 'failed'
                          ? 'Upload failed'
                          : 'Uploading document'}
                    </div>
                    <div className={styles.uploadProgressMeta}>
                      {chatUploadProgress?.fileName || 'Please wait...'}
                    </div>
                    <div className={styles.uploadProgressMeta}>
                      {typeof chatUploadProgress?.progressPercent === 'number'
                        ? `${chatUploadProgress.progressPercent}%`
                        : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
      </div>

      <div className={styles.chatFooter}>
        <form className={styles.inputForm}>
          <div className={styles.inputContainer}>
            {isRecording ? (
              <textarea
                value={interimTranscript || transcriptText || 'Listening...'}
                readOnly
                className={`${styles.inputField} ${styles.hiddenScroll} ${
                  interimTranscript ? styles.inputFieldInterim : ''
                }`}
                rows={1}
              />
            ) : (
              <textarea
                type="text"
                disabled={isTyping}
                value={inputValue.content}
                onChange={handleInputChange}
                ref={chatPromptRef}
                placeholder={t('chat:inputPlaceholder')}
                className={`${styles.inputField} ${styles.hiddenScroll}`}
                onKeyDown={handleKeyPress}
                rows={1}
              />
            )}

            <button
              type="button"
              onClick={handleToggleRecording}
              className={`${styles.micButton} ${
                isRecording ? styles.micButtonActive : ''
              }`}
            >
              {isRecording ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z" />
                  <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z" />
                </svg>
              )}
            </button>

            <div className={styles.fileButtonWrap}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              <img
                src={selectDocumentIcon}
                alt="Select Document"
                className={styles.fileButton}
                onClick={!isTyping ? handleFileUpload : undefined}
              />
            </div>

            <button
              onClick={(e) => {
                if (isRecording && transcriptText) {
                  handleSendTranscript();
                } else {
                  if (chatPromptRef.current) {
                    chatPromptRef.current.style.height = 'auto';
                  }
                  handleSendMessage(e, inputValue);
                }
              }}
              disabled={
                isLoading ||
                isMessageStreaming ||
                isTyping ||
                isSendingRef.current ||
                (isRecording && !transcriptText) ||
                (!isRecording && !inputValue.content)
              }
              className={`${styles.sendButton} ${
                isLoading ||
                isMessageStreaming ||
                isTyping ||
                (isRecording && !transcriptText) ||
                (!isRecording && !inputValue.content)
                  ? styles.sendButtonDisabled
                  : ''
              }`}
            >
              {t('chat:send')}
            </button>
          </div>
        </form>
        {speechError && <p className={styles.errorMessage}>{speechError}</p>}
        {/* <p className={styles.poweredByText}>Powered by Mira AI</p> */}
        <div className={styles.disclaimer}>
          {/* <img src="/fundmoreai.png" alt="" className={styles.disclaimerIcon} /> */}
          <p className={styles.disclaimerText}>{t('chat:disclaimer')}</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <>
          <button
            type="button"
            className={`${styles.chatBackdrop} ${
              isMobileChatOpen ? styles.chatBackdropOpen : ''
            }`}
            onClick={() => setIsMobileChatOpen(false)}
            aria-label="Close chat"
          />
          <section
            className={`${styles.chatSheet} ${
              isMobileChatOpen ? styles.chatSheetOpen : ''
            }`}
            aria-hidden={!isMobileChatOpen}
          >
            <div className={styles.chatSheetPanel}>
              {renderChatPanel({
                isDesktop: false,
                isTablet: false,
                onClose: () => setIsMobileChatOpen(false),
                onTabletClose: () => {},
              })}
            </div>
          </section>
        </>
      ) : (
        <>
          {isTabletView && isTabletChatHidden ? (
            <button
              type="button"
              className={styles.tabletChatReopen}
              onClick={() => setIsTabletChatHidden(false)}
              aria-label="Reopen chat"
              title="Reopen chat"
            >
              <span className={styles.tabletChatReopenIconWrap}>
                <Maximize2 className={styles.tabletChatReopenIcon} />
              </span>
              <img
                src="/fundmoreai.png"
                alt="Open chat"
                className={styles.tabletChatReopenLogo}
              />
            </button>
          ) : (
            <aside
              className={`${styles.chatAside} ${
                isChatExpanded && !isTabletView ? styles.chatAsideExpanded : ''
              }`}
            >
              {renderChatPanel({
                isDesktop: true,
                isTablet: isTabletView,
                onClose: () => {},
                onTabletClose: () => setIsTabletChatHidden(true),
              })}
            </aside>
          )}
        </>
      )}
      <ChatHistory
        userId={userId}
        organizationId={organizationId}
        isB2C={derivedUserRole === 'consumer'}
        isMobile={isMobile}
        isMobileChatOpen={isMobileChatOpen}
        isTabletChatHidden={isTabletChatHidden}
        setMessages={setMessages}
        setChatIds={setChatIds}
        isVectorClicks={isVectorClicks}
        setVectorClicks={setVectorClicks}
        setIsNewChat={setIsNewChat}
        setNewChatOpens={setNewChatOpens}
        isNewChatOpens={isNewChatOpens}
        isChatExpanded={isMobile ? false : isChatExpanded}
        setChatHistorySelected={setChatHistorySelected}
        setNewChatSelected={setNewChatSelected}
        setIsHistoryLoading={setIsHistoryLoading}
        isSessionBusy={isTyping || isMessageStreaming || isDocumentUploading}
      />
    </>
  );
};

export default ChatModel;
